import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  InputAdornment,
  IconButton,
  useTheme,
  Autocomplete,
  FormHelperText,
  RadioGroup,
  Typography,
  Box,
  Avatar,
  Chip,
  Card,
  CardContent,
  Paper,
  Stack,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ArrowBackOutlined,
  ArrowForwardOutlined,
  Visibility,
  VisibilityOff,
  PersonAdd,
  Edit,
  Lock,
  Person,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Work,
  AccountBalance,
  Star,
  UploadFile,
  Close,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import moment from "moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url,
).toString();

const nama_bankOptions = [
  { value: "BCA", text: "Bank Central Asia" },
  { value: "MANDIRI", text: "Bank Mandiri" },
  { value: "BRI", text: "Bank Rakyat Indonesia" },
  { value: "BNI", text: "Bank Negara Indonesia" },
  { value: "BTN", text: "Bank Tabungan Negara" },
  { value: "CIMBNIAGA", text: "CIMB Niaga" },
  { value: "DANAMON", text: "Bank Danamon" },
  { value: "OCBCNISP", text: "OCBC NISP" },
  { value: "PERMATA", text: "Bank Permata" },
  { value: "UOB", text: "UOB Indonesia" },
  { value: "SEABANK", text: "Seabank" },
  { value: "GOPAY", text: "GoPay" },
  { value: "OVO", text: "OVO" },
  { value: "DANA", text: "DANA" },
  { value: "LINKAJA", text: "LinkAja" },
  { value: "SHOPEEPAY", text: "ShopeePay" },
];

const tipeAgunanOptions = [
  { value: "Ijazah SMP", text: "Ijazah SMP" },
  { value: "Ijazah SMA", text: "Ijazah SMA" },
  { value: "Ijazah Sarjana", text: "Ijazah Sarjana" },
];

const genderOptions = [
  { value: "laki-laki", text: "Laki-Laki" },
  { value: "perempuan", text: "Perempuan" },
];

const KaryawanBaruModal = ({ open, onClose, onSubmit, dataEdit, editData }) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [disableTanggal, setDisableTanggal] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const { list_jabatan } = useSelector((state) => state.jabatan);
  const { list_karyawan } = useSelector((state) => state.karyawan);

  const isTambahData = dataEdit == null && !editData;
  const isEditData = dataEdit && editData;
  const isGantiPass = dataEdit && !editData;

  const validationSchema = Yup.object().shape({
    nik: Yup.string().when([], {
      is: () => isTambahData || isEditData,
      then: (schema) =>
        schema
          .matches(/^\d{16}$/, "NIK harus 16 digit angka")
          .required("NIK wajib diisi"),
      otherwise: (schema) => schema.notRequired(),
    }),
    nama: Yup.string().when([], {
      is: () => isTambahData || isEditData,
      then: (schema) =>
        schema
          .matches(/^[a-zA-Z\s]+$/, "Nama hanya boleh huruf")
          .required("Nama wajib diisi"),
      otherwise: (schema) => schema.notRequired(),
    }),
    email: Yup.string().when([], {
      is: () => isTambahData || isEditData,
      then: (schema) =>
        schema
          .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            "Format email tidak valid",
          )
          .required("Email wajib diisi"),
      otherwise: (schema) => schema.notRequired(),
    }),
    jenis_kelamin: Yup.string().when([], {
      is: () => isTambahData || isEditData,
      then: (schema) =>
        schema
          .oneOf(
            genderOptions.map((opt) => opt.value),
            "Jenis Kelamin tidak valid",
          )
          .required("Jenis Kelamin wajib diisi"),
      otherwise: (schema) => schema.notRequired(),
    }),
    kode_jabatan: Yup.string().when([], {
      is: () => isTambahData || isEditData,
      then: (schema) => schema.required("Jabatan wajib dipilih"),
      otherwise: (schema) => schema.notRequired(),
    }),
    no_hp: Yup.string().when([], {
      is: () => isTambahData || isEditData,
      then: (schema) =>
        schema
          .matches(
            /^08\d{8,11}$/,
            "Nomor HP harus diawali 08 dan terdiri dari 10-13 digit",
          )
          .required("Nomor HP wajib diisi"),
      otherwise: (schema) => schema.notRequired(),
    }),
    password: Yup.string().when([], {
      is: () => isTambahData || isGantiPass,
      then: (schema) =>
        schema
          .min(8, "Password minimal 8 karakter")
          .required("Password wajib diisi"),
      otherwise: (schema) => schema.notRequired(),
    }),
    file_jenjang: Yup.mixed().when([], {
      is: () => isTambahData,
      then: (schema) =>
        schema
          .required("File Kontrak wajib dipilih")
          .test(
            "fileSize",
            "Ukuran file maksimal 5MB",
            (value) => !value || (value && value.size <= 5000000),
          )
          .test(
            "fileType",
            "Format file harus PDF",
            (value) => !value || (value && value.type === "application/pdf"),
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const baseFormValues = {
    nik: "",
    nama: "",
    email: "",
    jenis_kelamin: "",
    alamat: "",
    tanggal_lahir: moment(),
    tanggal_mulai_kerja: moment(),
    tanggal_akhir_kerja: moment().add(3, "months"),
    tipe_agunan: "",
    nama_bank: "",
    atas_nama_rekening: "",
    nomor_rekening: "",
    kode_jabatan: "",
    cabang: "",
    no_hp: "",
    sikap: 1,
    keterampilan: 1,
    pengetahuan: 1,
    file_jenjang: null,
  };

  const initialValues = isTambahData
    ? {
        ...baseFormValues,
        password: "",
      }
    : isEditData
      ? {
          ...baseFormValues,
          password: "",
        }
      : {
          password: "",
        };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values);
      formik.resetForm();
      onClose();
    },
  });

  useEffect(() => {
    if (open && isEditData) {
      formik.setValues({
        nik: dataEdit.nik || "",
        nama: dataEdit.nama_karyawan || "",
        email: dataEdit.email || "",
        jenis_kelamin: dataEdit.jenis_kelamin?.toLowerCase() || "",
        alamat: dataEdit.alamat || "",
        tanggal_lahir: moment(dataEdit?.tanggal_lahir) || moment(),
        tanggal_mulai_kerja: moment(dataEdit?.tanggal_mulai_kerja) || moment(),
        tipe_agunan: dataEdit.tipe_agunan || "",
        nama_bank: dataEdit.nama_bank || "",
        atas_nama_rekening: dataEdit.atas_nama_rekening || "",
        nomor_rekening: dataEdit.nomor_rekening || "",
        kode_jabatan: dataEdit.kode_jabatan || "",
        cabang: dataEdit?.nama_jabatan?.split(" - ")[1] || "",
        no_hp: dataEdit.no_hp || "",
        sikap: dataEdit.sikap || "",
        keterampilan: dataEdit.keterampilan || "",
        pengetahuan: dataEdit.pengetahuan || "",
      });
    } else if (open && isGantiPass) {
      formik.setValues({
        password: "",
      });
    } else if (open && dataEdit == null) {
      formik.resetForm();
    }
  }, [open, dataEdit, formik.setValues, formik.resetForm]);

  if (!open) {
    return null;
  }

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];

    if (file) {
      formik.setFieldValue("file_jenjang", file);
      setFilePreview(file);
      setNumPages(null);
      setPageNumber(1);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber((prevPageNumber) => Math.max(1, prevPageNumber - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prevPageNumber) =>
      Math.min(numPages || 1, prevPageNumber + 1),
    );
  };

  const renderFilePreview = () => {
    if (!filePreview) return null;

    const fileType = filePreview.type;

    if (fileType === "application/pdf") {
      return (
        <Paper
          sx={{
            p: 2,
            mt: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Document
            file={filePreview}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => console.error("PDF load error", error)}
          >
            <Page pageNumber={pageNumber} />
          </Document>
          {numPages > 1 && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mt={2}
            >
              <Button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                size="small"
                variant="outlined"
              >
                <ArrowBackOutlined />
              </Button>
              <Typography variant="body2">
                {pageNumber} / {numPages}
              </Typography>
              <Button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                size="small"
                variant="outlined"
              >
                <ArrowForwardOutlined />
              </Button>
            </Stack>
          )}
        </Paper>
      );
    } else if (fileType.startsWith("image/")) {
      return (
        <Paper
          sx={{
            p: 2,
            mt: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <img
            src={URL.createObjectURL(filePreview)}
            alt="Preview"
            style={{ maxWidth: "100%", maxHeight: "300px" }}
          />
        </Paper>
      );
    } else {
      return (
        <Typography variant="body2" color="text.secondary">
          Preview tidak tersedia untuk tipe ini.
        </Typography>
      );
    }
  };

  const SectionHeader = ({ icon, title }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 2,
        mt: 1,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        px: 2,
        py: 1.5,
        backgroundColor: `${theme.palette.background.paper}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: 1.5,
          backgroundColor: `${theme.palette.primary.main}15`,
          mr: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
  );

  const RatingCard = ({ label, name, value, onChange, error, helperText }) => (
    <Card
      sx={{
        mb: 2,
        border: 1,
        borderColor: error ? "error.main" : "divider",
        backgroundColor: `${theme.palette.secondary.main}08`,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1}
        >
          {label}
        </Typography>
        <RadioGroup row value={value} onChange={onChange} name={name}>
          {[1, 2, 3, 4, 5, 6].map((val) => (
            <Chip
              key={val}
              label={val}
              onClick={() => onChange({ target: { name, value: val } })}
              sx={{
                mr: 0.5,
                cursor: "pointer",
                backgroundColor:
                  value === val ? theme.palette.secondary.main : "default",
                color: value === val ? "white" : "default",
                "&:hover": {
                  backgroundColor:
                    value === val
                      ? theme.palette.secondary.main
                      : `${theme.palette.secondary.main}30`,
                },
              }}
            />
          ))}
        </RadioGroup>
        {error && (
          <FormHelperText error sx={{ mt: 1 }}>
            {helperText}
          </FormHelperText>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: `${theme.palette.primary.main}08`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", p: 3 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              mr: 2,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            {isTambahData && <PersonAdd />}
            {isEditData && <Edit />}
            {isGantiPass && <Lock />}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {isTambahData && "Tambah Karyawan Baru"}
              {isEditData && "Edit Data Karyawan"}
              {isGantiPass && "Ganti Password"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isTambahData &&
                "Lengkapi formulir di bawah untuk menambahkan karyawan baru"}
              {isEditData && "Perbarui informasi karyawan yang ada"}
              {isGantiPass && "Masukkan password baru untuk karyawan ini"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {isTambahData && (
              <>
                {/* Informasi Pribadi */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Person fontSize="small" />}
                    title="Informasi Pribadi"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="NIK"
                    name="nik"
                    value={formik.values.nik}
                    onChange={formik.handleChange}
                    error={formik.touched.nik && Boolean(formik.errors.nik)}
                    helperText={formik.touched.nik && formik.errors.nik}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nama"
                    name="nama"
                    value={formik.values.nama}
                    onChange={formik.handleChange}
                    error={formik.touched.nama && Boolean(formik.errors.nama)}
                    helperText={formik.touched.nama && formik.errors.nama}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Jenis Kelamin"
                    name="jenis_kelamin"
                    value={formik.values.jenis_kelamin}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.jenis_kelamin &&
                      Boolean(formik.errors.jenis_kelamin)
                    }
                    helperText={
                      formik.touched.jenis_kelamin &&
                      formik.errors.jenis_kelamin
                    }
                  >
                    {genderOptions.map(({ text, value }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nomor HP"
                    name="no_hp"
                    value={formik.values.no_hp}
                    onChange={formik.handleChange}
                    error={formik.touched.no_hp && Boolean(formik.errors.no_hp)}
                    helperText={formik.touched.no_hp && formik.errors.no_hp}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      sx={{ width: "100%" }}
                      label="Tanggal Lahir"
                      value={formik.values.tanggal_lahir}
                      onChange={(val) =>
                        formik.setFieldValue("tanggal_lahir", val)
                      }
                      slotProps={{
                        textField: {
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarToday
                                  fontSize="small"
                                  sx={{ color: "text.secondary" }}
                                />
                              </InputAdornment>
                            ),
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Alamat"
                    name="alamat"
                    value={formik.values.alamat}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.alamat && Boolean(formik.errors.alamat)
                    }
                    helperText={formik.touched.alamat && formik.errors.alamat}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn
                            fontSize="small"
                            sx={{ color: "text.secondary", mt: 1 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Informasi Pekerjaan */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Work fontSize="small" />}
                    title="Informasi Pekerjaan"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      sx={{ width: "100%" }}
                      label="Tanggal Mulai Kerja"
                      value={formik.values.tanggal_mulai_kerja}
                      onChange={(val) => {
                        formik.setFieldValue("tanggal_mulai_kerja", val);
                        if (val && val.isValid()) {
                          const tanggalAkhir = val.clone().add(3, "months");
                          formik.setFieldValue(
                            "tanggal_akhir_kerja",
                            tanggalAkhir,
                          );
                        } else {
                          formik.setFieldValue("tanggal_akhir_kerja", null);
                        }
                      }}
                      slotProps={{
                        textField: {
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarToday
                                  fontSize="small"
                                  sx={{ color: "text.secondary" }}
                                />
                              </InputAdornment>
                            ),
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={list_jabatan}
                    getOptionLabel={(option) =>
                      `${option.kode_jabatan} - ${option.nama_jabatan}`
                    }
                    value={
                      list_jabatan.find(
                        (item) =>
                          item.kode_jabatan === formik.values.kode_jabatan,
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      formik.setFieldValue(
                        "kode_jabatan",
                        newValue?.kode_jabatan || "",
                      );
                      formik.setFieldValue(
                        "cabang",
                        newValue?.nama_jabatan.split(" - ")[1] || "",
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Jabatan"
                        name="kode_jabatan"
                        error={
                          formik.touched.kode_jabatan &&
                          Boolean(formik.errors.kode_jabatan)
                        }
                        helperText={
                          formik.touched.kode_jabatan &&
                          formik.errors.kode_jabatan
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Work
                                fontSize="small"
                                sx={{ color: "text.secondary" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Informasi Bank */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<AccountBalance fontSize="small" />}
                    title="Informasi Bank"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Tipe Agunan"
                    name="tipe_agunan"
                    value={formik.values.tipe_agunan}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.tipe_agunan &&
                      Boolean(formik.errors.tipe_agunan)
                    }
                    helperText={
                      formik.touched.tipe_agunan && formik.errors.tipe_agunan
                    }
                  >
                    {tipeAgunanOptions.map(({ text, value }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Nama Bank"
                    name="nama_bank"
                    value={formik.values.nama_bank}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.nama_bank &&
                      Boolean(formik.errors.nama_bank)
                    }
                    helperText={
                      formik.touched.nama_bank && formik.errors.nama_bank
                    }
                  >
                    {nama_bankOptions.map(({ text, value }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Atas Nama Rekening"
                    name="atas_nama_rekening"
                    value={formik.values.atas_nama_rekening}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.atas_nama_rekening &&
                      Boolean(formik.errors.atas_nama_rekening)
                    }
                    helperText={
                      formik.touched.atas_nama_rekening &&
                      formik.errors.atas_nama_rekening
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nomor Rekening"
                    name="nomor_rekening"
                    value={formik.values.nomor_rekening}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.nomor_rekening &&
                      Boolean(formik.errors.nomor_rekening)
                    }
                    helperText={
                      formik.touched.nomor_rekening &&
                      formik.errors.nomor_rekening
                    }
                  />
                </Grid>

                {/* Password */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Lock fontSize="small" />}
                    title="Keamanan"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Kompetensi */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Star fontSize="small" />}
                    title="Kompetensi"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <RatingCard
                    label="Sikap"
                    name="sikap"
                    value={formik.values.sikap}
                    onChange={formik.handleChange}
                    error={formik.touched.sikap && Boolean(formik.errors.sikap)}
                    helperText={formik.touched.sikap && formik.errors.sikap}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <RatingCard
                    label="Keterampilan"
                    name="keterampilan"
                    value={formik.values.keterampilan}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.keterampilan &&
                      Boolean(formik.errors.keterampilan)
                    }
                    helperText={
                      formik.touched.keterampilan && formik.errors.keterampilan
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <RatingCard
                    label="Pengetahuan"
                    name="pengetahuan"
                    value={formik.values.pengetahuan}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.pengetahuan &&
                      Boolean(formik.errors.pengetahuan)
                    }
                    helperText={
                      formik.touched.pengetahuan && formik.errors.pengetahuan
                    }
                  />
                </Grid>

                {/* File Upload */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<UploadFile fontSize="small" />}
                    title="Dokumen"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 3,
                      border: 2,
                      borderColor: "divider",
                      borderStyle: "dashed",
                      textAlign: "center",
                      cursor: "pointer",
                      transition: "all 0.3s",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: `${theme.palette.primary.main}08`,
                      },
                    }}
                    onClick={() =>
                      document.getElementById("file-upload").click()
                    }
                  >
                    <input
                      id="file-upload"
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={handleFileChange}
                    />
                    <UploadFile
                      sx={{
                        fontSize: 48,
                        color: "text.secondary",
                        mb: 1,
                      }}
                    />
                    <Typography variant="body1" gutterBottom>
                      Klik untuk upload file kontrak
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Format: PDF (Maksimal 5MB)
                    </Typography>
                    {filePreview && (
                      <Chip
                        label={filePreview.name}
                        onDelete={() => {
                          setFilePreview(null);
                          formik.setFieldValue("file_jenjang", null);
                        }}
                        sx={{ mt: 2 }}
                        deleteIcon={<Close />}
                      />
                    )}
                  </Paper>
                  {formik.touched.file_jenjang &&
                    formik.errors.file_jenjang && (
                      <Typography color="error" variant="body2" mt={1}>
                        {formik.errors.file_jenjang}
                      </Typography>
                    )}
                </Grid>
                <Grid item xs={12}>
                  {renderFilePreview()}
                </Grid>
              </>
            )}
            {isEditData && (
              <>
                {/* Informasi Pribadi */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Person fontSize="small" />}
                    title="Informasi Pribadi"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="NIK"
                    name="nik"
                    value={formik.values.nik}
                    onChange={formik.handleChange}
                    error={formik.touched.nik && Boolean(formik.errors.nik)}
                    helperText={formik.touched.nik && formik.errors.nik}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nama"
                    name="nama"
                    value={formik.values.nama}
                    onChange={formik.handleChange}
                    error={formik.touched.nama && Boolean(formik.errors.nama)}
                    helperText={formik.touched.nama && formik.errors.nama}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Jenis Kelamin"
                    name="jenis_kelamin"
                    value={formik.values.jenis_kelamin}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.jenis_kelamin &&
                      Boolean(formik.errors.jenis_kelamin)
                    }
                    helperText={
                      formik.touched.jenis_kelamin &&
                      formik.errors.jenis_kelamin
                    }
                  >
                    {genderOptions.map(({ text, value }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nomor HP"
                    name="no_hp"
                    value={formik.values.no_hp}
                    onChange={formik.handleChange}
                    error={formik.touched.no_hp && Boolean(formik.errors.no_hp)}
                    helperText={formik.touched.no_hp && formik.errors.no_hp}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      sx={{ width: "100%" }}
                      label="Tanggal Lahir"
                      value={formik.values.tanggal_lahir}
                      onChange={(val) =>
                        formik.setFieldValue("tanggal_lahir", val)
                      }
                      slotProps={{
                        textField: {
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarToday
                                  fontSize="small"
                                  sx={{ color: "text.secondary" }}
                                />
                              </InputAdornment>
                            ),
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Alamat"
                    name="alamat"
                    value={formik.values.alamat}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.alamat && Boolean(formik.errors.alamat)
                    }
                    helperText={formik.touched.alamat && formik.errors.alamat}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn
                            fontSize="small"
                            sx={{ color: "text.secondary", mt: 1 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Informasi Pekerjaan */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Work fontSize="small" />}
                    title="Informasi Pekerjaan"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      sx={{ width: "100%" }}
                      label="Tanggal Mulai Kerja"
                      value={formik.values.tanggal_mulai_kerja}
                      onChange={(val) =>
                        formik.setFieldValue("tanggal_mulai_kerja", val)
                      }
                      slotProps={{
                        textField: {
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarToday
                                  fontSize="small"
                                  sx={{ color: "text.secondary" }}
                                />
                              </InputAdornment>
                            ),
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={list_jabatan}
                    getOptionLabel={(option) =>
                      `${option.kode_jabatan} - ${option.nama_jabatan}`
                    }
                    value={
                      list_jabatan.find(
                        (item) =>
                          item.kode_jabatan === formik.values.kode_jabatan,
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      formik.setFieldValue(
                        "kode_jabatan",
                        newValue?.kode_jabatan || "",
                      );
                      formik.setFieldValue(
                        "cabang",
                        newValue?.nama_jabatan.split(" - ")[1] || "",
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Jabatan"
                        name="kode_jabatan"
                        error={
                          formik.touched.kode_jabatan &&
                          Boolean(formik.errors.kode_jabatan)
                        }
                        helperText={
                          formik.touched.kode_jabatan &&
                          formik.errors.kode_jabatan
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Work
                                fontSize="small"
                                sx={{ color: "text.secondary" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Informasi Bank */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<AccountBalance fontSize="small" />}
                    title="Informasi Bank"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Tipe Agunan"
                    name="tipe_agunan"
                    value={formik.values.tipe_agunan}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.tipe_agunan &&
                      Boolean(formik.errors.tipe_agunan)
                    }
                    helperText={
                      formik.touched.tipe_agunan && formik.errors.tipe_agunan
                    }
                  >
                    {tipeAgunanOptions.map(({ text, value }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Nama Bank"
                    name="nama_bank"
                    value={formik.values.nama_bank}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.nama_bank &&
                      Boolean(formik.errors.nama_bank)
                    }
                    helperText={
                      formik.touched.nama_bank && formik.errors.nama_bank
                    }
                  >
                    {nama_bankOptions.map(({ text, value }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Atas Nama Rekening"
                    name="atas_nama_rekening"
                    value={formik.values.atas_nama_rekening}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.atas_nama_rekening &&
                      Boolean(formik.errors.atas_nama_rekening)
                    }
                    helperText={
                      formik.touched.atas_nama_rekening &&
                      formik.errors.atas_nama_rekening
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nomor Rekening"
                    name="nomor_rekening"
                    value={formik.values.nomor_rekening}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.nomor_rekening &&
                      Boolean(formik.errors.nomor_rekening)
                    }
                    helperText={
                      formik.touched.nomor_rekening &&
                      formik.errors.nomor_rekening
                    }
                  />
                </Grid>

                {/* Kompetensi */}
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Star fontSize="small" />}
                    title="Kompetensi"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <RatingCard
                    label="Sikap"
                    name="sikap"
                    value={formik.values.sikap}
                    onChange={formik.handleChange}
                    error={formik.touched.sikap && Boolean(formik.errors.sikap)}
                    helperText={formik.touched.sikap && formik.errors.sikap}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <RatingCard
                    label="Keterampilan"
                    name="keterampilan"
                    value={formik.values.keterampilan}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.keterampilan &&
                      Boolean(formik.errors.keterampilan)
                    }
                    helperText={
                      formik.touched.keterampilan && formik.errors.keterampilan
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <RatingCard
                    label="Pengetahuan"
                    name="pengetahuan"
                    value={formik.values.pengetahuan}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.pengetahuan &&
                      Boolean(formik.errors.pengetahuan)
                    }
                    helperText={
                      formik.touched.pengetahuan && formik.errors.pengetahuan
                    }
                  />
                </Grid>
              </>
            )}
            {isGantiPass && (
              <>
                <Grid item xs={12}>
                  <SectionHeader
                    icon={<Lock fontSize="small" />}
                    title="Keamanan"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: 1, borderColor: "divider" }}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Simpan
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default KaryawanBaruModal;
